import * as React from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../../store';
import * as ProjectsStore from '../../../../store/projects';
import * as RuntimeStore from '../../../../store/runtime';
import * as CommonValuesStore from '../../../../store/common-values';
import { bindActionCreators } from 'redux';
import ProjectNewForm from './ProjectNewForm';
import { UserState } from 'redux-oidc';
import { ConnectedRouterProps } from 'connected-react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '@mui/material';
import { Project } from '../../../../store/projects';
import { Company, Person } from '../../../../store/common-values/commonTypes';
import { apiRequest } from '../../../../utils/api';
import GoBackContainer from './../../../application/GoBackContainer';

// At runtime, Redux will merge together...
type ProjectNewFormProps =
    & typeof RuntimeStore.actionCreators
    & ProjectsStore.ProjectsState
    & typeof ProjectsStore.actionCreators
    & CommonValuesStore.CommmonValuesState
    & typeof CommonValuesStore.actionCreators
    & ConnectedRouterProps
    & UserState;

class ProjectNewFormContainer extends React.PureComponent<ProjectNewFormProps> {
    // This method is called when the component is first added to the document
    public componentDidMount() {
        this.ensureDataFetched();
    }

    componentDidUpdate(prevProps: ProjectNewFormProps) {
        //Check if item was saved
        if (this.props.projectSaved && prevProps.projectSaved != this.props.projectSaved) {
            this.goBack();
            this.props.showNotification("Project was added successfully", "info");
        }
    }

    private createProject(project: Project): void {
        this.props.addProject(project);
    }

    private cancel(): void {
        this.goBack();
    }

    private goBack(): void {
        if (this.props.history.length > 0)
            this.props.history.goBack();
        else
            this.props.history.replace("/projects");
    }

    private async getCompaniesByName(name: string): Promise<Company[]> {
        try {
            const data = await apiRequest(`/api/commonvalues/companiesbyname?name=${name}`, this.props.user) as Company[];
            return data;
        } catch (error) {
            this.props.showNotification("Could not retrieve companies", "error");
            return [];
        }
    }

    private async getCompaniesByNumber(number: string): Promise<Company[]> {
        try {
            const data = await apiRequest(`/api/commonvalues/companiesbynumber?number=${number}`, this.props.user) as Company[];
            return data;
        } catch (error) {
            this.props.showNotification("Could not retrieve companies", "error");
            return [];
        }
    }

    private async getPersonsByName(name: string): Promise<Person[]> {
        try {
            const data = await apiRequest(`/api/commonvalues/personsbyname?name=${name}`, this.props.user) as Person[];
            return data;
        } catch (error) {
            this.props.showNotification("Could not retrieve persons", "error");
            return [];
        }
    }

    private async getPersonsByEmail(email: string): Promise<Person[]> {
        try {
            const data = await apiRequest(`/api/commonvalues/personsbyemail?email=${email}`, this.props.user) as Person[];
            return data;
        } catch (error) {
            this.props.showNotification("Could not retrieve persons", "error");
            return [];
        }
    }

    public render() {
        return (
            <React.Fragment>
                <GoBackContainer title="Prosjektregistrering" onGoBack={() => this.goBack()}>
                    <ProjectNewForm
                        onSave={(i) => this.createProject(i)}
                        onCancel={() => this.cancel()}
                        isLoading={this.props.isLoading}
                        getCompaniesByName={(name: string) => this.getCompaniesByName(name)}
                        getCompaniesByNumber={(number: string) => this.getCompaniesByNumber(number)}
                        getPersonsByName={(name: string) => this.getPersonsByName(name)}
                        getPersonsByEmail={(email: string) => this.getPersonsByEmail(email)}
                        regions={this.props.regions}
                        breeamVersions={this.props.breeamVersions}
                        buildingTypes={this.props.buildingTypes}
                        projectTypes={this.props.projectTypes}
                        sizeTypes={this.props.sizeTypes}
                        auditPhases={this.props.auditPhases}
                        furnishedStatuses={this.props.furnishedStatuses}
                        aps={this.props.aps}
                    />
                </GoBackContainer>
            </React.Fragment>
        );
    }

    private ensureDataFetched() {
        this.props.resetFormState();
        this.props.ensureBreeamVersions();
        this.props.ensureBreeamSubjects();
        this.props.ensureRegions();
        this.props.ensureBuildingTypes();
        this.props.ensureProjectTypes();
        this.props.ensureSizeTypes();
        this.props.ensureAuditPhases();
        this.props.ensureFurnishedStatuses();
        this.props.ensureAps();
    }
}

export default connect(
    (state: RootState) => {
        const { commonValues, oidc, projects } = state;
        return {
            ...projects,
            ...commonValues,
            ...oidc
        };
    },
    (dispatch) => {
        return {
            ...bindActionCreators(
                Object.assign(
                    {},
                    ProjectsStore.actionCreators,
                    RuntimeStore.actionCreators,
                    CommonValuesStore.actionCreators
                ), dispatch)
        };
    }
)(ProjectNewFormContainer as any); // eslint-disable-line @typescript-eslint/no-explicit-any
